import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Edit, Mail, MapPin, Phone, User } from "lucide-react";
import { customerService } from "@/lib/database";
import type { Customer } from "@/lib/supabase";

export default function Customer() {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomer();
  }, []);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      const customer = await customerService.getById(customerId!);

      setCustomer(customer);
    } catch (error) {
      console.error("Error loading customer:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/customers/${customerId}/edit`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading customer...</div>
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between space-x-2">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {customer?.first_name} {customer?.last_name}
            </h1>
            <p className="text-gray-600">Viewing details for this customer</p>
          </div>
        </div>

        <Button onClick={handleEdit}>
          <Edit className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>
              Basic customer details and contact information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span>{customer.first_name}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span>{customer.last_name}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>

                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="break-all">{customer.email}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                {customer.phone ? (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{customer.phone}</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500 text-sm">
                      No phone number provided
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Addresses */}
        <Card>
          <CardHeader>
            <CardTitle>Addresses</CardTitle>
            <CardDescription>Customer shipping addresses</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="us_address">US Address</Label>
              {customer.us_address ? (
                <div className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 text-gray-400 mt-1" />
                  <span>{customer.us_address}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">No US address provided</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="haiti_address">Haiti Address</Label>
              {customer.haiti_address ? (
                <div className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 text-gray-400 mt-1" />
                  <span>{customer.haiti_address}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">
                    No Haiti address provided
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        {/* <Card>
            <CardHeader>
              <CardTitle>Preferences</CardTitle>
              <CardDescription>Customer preferences and account settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 space-x-2">
                  <Label htmlFor="preferred_language">Preferred Language</Label>

                  <Badge variant="outline" className="capitalize">
                    {customer.preferred_language}
                  </Badge>
                </div>

                <div className="space-y-2 space-x-2">
                  <Label htmlFor="notification_method">Notification Method</Label>

                  <Badge variant="outline" className="capitalize">
                    {customer.notification_method}
                  </Badge>
                </div>

                <div className="space-y-2 space-x-2">
                  <Label htmlFor="preferred_shipping_method">Preferred Shipping</Label>
                  <Badge variant="outline" className="capitalize">
                    {customer.preferred_shipping_method}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 space-x-2">
                  <Label>Account Status</Label>
                  <Badge className={customer.account_status === 'active' ? 'bg-green-100 hover:bg-green-100 text-green-800' : 'bg-red-100 hover:bg-red-100 text-red-800'}>
                    {customer.account_status}
                  </Badge>
                </div>

                {/* <div className="space-y-2 space-x-2">
                  <Label>Customer Tier</Label>
                  <Badge className={customer.customer_tier === 'premium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}>
                    {customer.customer_tier}
                  </Badge>
                </div>
              </div>

              {customer.special_instructions && (
                <div className="space-y-2 space-x-2">
                  <Label htmlFor="special_instructions">Special Instructions</Label>
                  <p className="text-sm text-gray-600">{customer.special_instructions}</p>
                </div>
              )}
            </CardContent>
          </Card> */}

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>
              Account creation and activity details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-gray-500">Created</Label>
                <p>{new Date(customer.created_at!).toLocaleDateString()}</p>
              </div>
              <div>
                <Label className="text-gray-500">Last Updated</Label>
                <p>{new Date(customer.updated_at!).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
